import { useEffect, useState } from 'react';
import api from '../utils/api';
import { Zap, Leaf, TreePine, Car, Factory, TrendingDown, Settings, RotateCcw } from 'lucide-react';
import PageHeader from '../components/PageHeader';

function StatCard({ icon: Icon, label, value, suffix, color = 'text-white', sub }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="text-gray-500" />
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
        {suffix && <span className="text-sm text-gray-500 ml-1">{suffix}</span>}
      </p>
      {sub && <p className="text-xs text-gray-600 mt-1">{sub}</p>}
    </div>
  );
}

export default function EnergyPage() {
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(30);
  const [showConfig, setShowConfig] = useState(false);
  const [cfgDraft, setCfgDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadReport = () => {
    api.get(`/energy/report?days=${days}`).then(r => setReport(r.data));
  };

  useEffect(() => {
    let cancelled = false;
    api.get(`/energy/report?days=${days}`).then(r => { if (!cancelled) setReport(r.data); });
    return () => { cancelled = true; };
  }, [days]);

  const openConfig = async () => {
    const r = await api.get('/costing/config');
    setCfgDraft(r.data);
    setShowConfig(true);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/costing/config', cfgDraft);
      setShowConfig(false);
      loadReport();
    } catch (e) {
      alert('저장 실패: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('에너지 계산 설정을 기본값으로 초기화하시겠습니까?')) return;
    setResetting(true);
    try {
      await api.post('/costing/config/reset');
      loadReport();
      window.alert('설정이 초기화되었습니다.');
    } catch (e) {
      window.alert('초기화 실패: ' + (e.response?.data?.message || e.message));
    } finally { setResetting(false); }
  };

  if (!report) return <div className="text-gray-500 text-sm">로딩 중...</div>;

  return (
    <div>
      <PageHeader icon={Leaf} title="에너지 · ESG 리포트">
        <div className="flex gap-1">
          {[7, 30, 90, 365].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-xs ${days === d ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400'}`}
            >{d === 365 ? '1년' : `${d}일`}</button>
          ))}
        </div>
        <button
          onClick={handleReset}
          disabled={resetting}
          className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 text-gray-500 hover:text-red-400 rounded-lg text-sm disabled:opacity-40"
        >
          <RotateCcw size={12} /> 초기화
        </button>
        <button
          onClick={openConfig}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-400 rounded-lg text-sm hover:text-white"
        >
          <Settings size={14} /> 계산 설정
        </button>
      </PageHeader>

      {/* 데이터 소스 배지 */}
      <div className="mb-4 flex items-center gap-2">
        <span className={`text-xs px-2 py-1 rounded-full ${
          report.mode === 'measured'
            ? 'bg-green-500/20 text-green-400'
            : 'bg-yellow-500/20 text-yellow-400'
        }`}>
          {report.mode === 'measured'
            ? `실측 데이터 (${report.readingsCount}개 포인트)`
            : '추정치 (잡 시간 × 정격 전력)'}
        </span>
        {report.mode === 'measured' && (
          <span className="text-xs text-gray-500">
            추정 대비: {report.estimatedKwh}kWh (실측 {report.measuredKwh}kWh)
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Zap}
          label="총 전력 소비"
          value={report.totalKwh}
          suffix="kWh"
          color="text-yellow-400"
          sub={`${report.jobCount}건 완료 잡 기준`}
        />
        <StatCard
          icon={Leaf}
          label="탄소 배출량"
          value={report.totalKgCO2}
          suffix="kg CO₂"
          color="text-green-400"
          sub="한국 전력망 0.4237 kg/kWh"
        />
        <StatCard
          icon={Factory}
          label="전기료"
          value={report.totalElectricityCost}
          suffix="원"
          sub="설정된 단가 기준"
        />
        <StatCard
          icon={TrendingDown}
          label="잡당 평균"
          value={report.perJobKwh}
          suffix="kWh"
          sub={`${report.perJobKgCO2} kg CO₂/잡`}
        />
      </div>

      {/* 탄소 환산 */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
        <h2 className="text-white font-semibold text-sm mb-4">탄소 배출 환산</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-4 p-4 bg-gray-800/50 rounded-lg">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <TreePine size={24} className="text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {report.equivalencies.treesMonthlyAbsorption.toLocaleString()}
                <span className="text-sm text-gray-500 ml-1">그루</span>
              </p>
              <p className="text-xs text-gray-500">이 배출량을 흡수하는 데 필요한 나무 (월간 기준)</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-gray-800/50 rounded-lg">
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Car size={24} className="text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {report.equivalencies.kmDriven.toLocaleString()}
                <span className="text-sm text-gray-500 ml-1">km</span>
              </p>
              <p className="text-xs text-gray-500">승용차 주행 거리에 해당 (0.192 kg/km)</p>
            </div>
          </div>
        </div>
      </div>

      {/* 생산 요약 */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h2 className="text-white font-semibold text-sm mb-4">생산 요약</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">완료 잡</p>
            <p className="text-lg font-bold text-white">{report.jobCount.toLocaleString()}건</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">총 필라멘트</p>
            <p className="text-lg font-bold text-white">{report.totalFilamentGram.toLocaleString()}g</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">잡당 전력</p>
            <p className="text-lg font-bold text-white">{report.perJobKwh} kWh</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">잡당 CO₂</p>
            <p className="text-lg font-bold text-white">{report.perJobKgCO2} kg</p>
          </div>
        </div>
      </div>

      {/* 설정 모달 */}
      {showConfig && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-md w-full">
            <h2 className="text-white font-semibold mb-1">에너지 계산 설정</h2>
            <p className="text-xs text-gray-500 mb-4">
              이 값들은 원가 설정과 공유됩니다. 스마트플러그 실측값이 수신되면 자동으로 실측 모드로 전환됩니다.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">프린터 정격 전력 (kW)</label>
                <input
                  type="number"
                  step="0.01"
                  value={cfgDraft.printerPowerKw ?? ''}
                  onChange={e => setCfgDraft(c => ({ ...c, printerPowerKw: parseFloat(e.target.value) || 0 }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                />
                <p className="text-xs text-gray-600 mt-1">Bambu Lab H2D 평균 0.35kW</p>
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">전기 단가 (원/kWh)</label>
                <input
                  type="number"
                  step="1"
                  value={cfgDraft.electricityPricePerKwh ?? ''}
                  onChange={e => setCfgDraft(c => ({ ...c, electricityPricePerKwh: parseFloat(e.target.value) || 0 }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                />
                <p className="text-xs text-gray-600 mt-1">산업용 기본 150원, 심야 80원 등</p>
              </div>
            </div>
            <div className="mt-5 p-3 bg-gray-800/50 rounded-lg text-xs text-gray-500 leading-relaxed">
              <p className="text-gray-400 mb-1">계산 공식</p>
              <p>• 전력 소비 = 출력 시간 × 프린터 정격 전력</p>
              <p>• 전기료 = 전력 소비 × 전기 단가</p>
              <p>• 탄소 배출 = 전력 소비 × 0.4237 kg CO₂ (한국 전력망 고정)</p>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setShowConfig(false)}
                className="flex-1 py-2 bg-gray-800 text-gray-400 rounded-lg text-sm"
              >취소</button>
              <button
                onClick={saveConfig}
                disabled={saving}
                className="flex-1 py-2 bg-brand text-white rounded-lg text-sm disabled:opacity-50"
              >{saving ? '저장 중...' : '저장'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
